/** Compact abbreviations for storey filters and narrow Results2 list tabs. */
export const elementTypeShortLabel: Partial<Record<ElementTypes, string>> = {
  columns: 'C',
  rips: 'WS',
  lintels: 'FS',
  beams: 'UZ',
  purlins: 'P',
  roof_slabs: 'DA',
  slabs: 'D',
  foundation: 'FU',
}

export const elementTypeToLabelSingular: Record<ElementTypes, string> = {
  beams: 'Unterzug',
  outer_walls: 'Außenwand',
  inner_walls: 'Innenwand',
  slabs: 'Decke',
  roof_slabs: 'Dach',
  vertical_slabs: 'Deckenposition',
  vertical_roof_slabs: 'Dachposition',
  columns: 'Stütze',
  purlins: 'Pfette',
  rips: 'Rippe',
  lintels: 'Sturz',
  slab_beams: 'Deckenbalken',
  roof_slab_beams: 'Dachbalken',
  standard_rips: 'Standardrippe',
  foundation: 'Fundament',
}

export const supportTypeToLabel: Record<SupportType, string> = {
  line: 'Linienauflager',
  point: 'Punktauflager',
}

export const memberCheckTypeToReadableMap: Record<MemberCheckType, string> = {
  BendingYZ: 'zweiachsige Biegung',
  BendingY: 'Biegung Y',
  BendingZ: 'Biegung Z',
  BendingWithTension: 'Biegung und Zug',
  BendingWithCompression: 'Biegung und Druck',
  TensionOnly: 'Zug',
  CompressionOnly: 'Druck',
  TwoWayShearStress: 'zweiachsiger Schub',
  ShearStressY: 'Schub Y',
  ShearStressZ: 'Schub Z',
  TwoWayShearStressCLTParallelLayer: 'Schub in Längslage',
  TwoWayShearStressCLTOrthogonalLayer: 'Rollschub',
  BendingYZHot: 'zweiachsige Biegung bei Brand',
  BendingWithTensionHot: 'Biegung und Zug bei Brand',
  BendingWithCompressionHot: 'Biegung und Druck bei Brand',
  TensionOnlyHot: 'Zug bei Brand',
  CompressionOnlyHot: 'Druck bei Brand',
  TwoWayShearStressHot: 'zweiachsiger Schub bei Brand',
  TwoWayShearStressCLTParallelLayerHot: 'Schub in Längslage bei Brand',
  TwoWayShearStressCLTOrthogonalLayerHot: 'Rollschub bei Brand',
  HotDimensioning: 'Querschnittsreduzierung bei Brand',
  HotDimensioningCLT: 'Querschnittsreduzierung bei Brand',
  DisplacementNetFinZ: 'Enddurchbiegung Z',
  DisplacementNetFinY: 'Enddurchbiegung Y',
  DisplacementNetFinYZ: 'zweiachsige Enddurchbiegung',
  DisplacementInstY: 'Durchbiegung Y',
  DisplacementInstZ: 'Durchbiegung Z',
  DisplacementInstYZ: 'Durchbiegung YZ',
  SupportCompression: 'Auflagerpressung',
  Vibration: 'Schwingung',
  SteelBasicCheck: 'Kombiniert',
  SteelDisplacement: 'Durchbiegung',
  SteelCompression: 'Druck',
  SteelSupportCompression: 'Auflagerpressung',
  SteelBasePlateWeld: 'Schweißnaht Fußplatte',
  FireProtectionRip: 'vereinfachte Heißbemessung Rippe',
}

/**
 * Short check-type label shown next to η in the Results2 list and detail-pane header,
 * grouping checks by the governing internal force (M = Biegung, N = Normalkraft,
 * Q = Schub, w = Durchbiegung …). `'-'` means no short label should be shown
 * (support compression, which is always rendered as the separate η_c,90 value).
 */
export const memberCheckTypeToShortLabel: Record<MemberCheckType, string> = {
  BendingYZ: 'M',
  BendingY: 'M',
  BendingZ: 'M',
  BendingWithTension: 'M',
  BendingWithCompression: 'M',
  TensionOnly: 'N',
  CompressionOnly: 'N',
  TwoWayShearStress: 'Q',
  ShearStressY: 'Q',
  ShearStressZ: 'Q',
  TwoWayShearStressCLTParallelLayer: 'Q',
  TwoWayShearStressCLTOrthogonalLayer: 'Q',
  BendingYZHot: 'M',
  BendingWithTensionHot: 'M',
  BendingWithCompressionHot: 'M',
  TensionOnlyHot: 'N',
  CompressionOnlyHot: 'N',
  TwoWayShearStressHot: 'Q',
  TwoWayShearStressCLTParallelLayerHot: 'Q',
  TwoWayShearStressCLTOrthogonalLayerHot: 'Q',
  HotDimensioning: 'QS',
  HotDimensioningCLT: 'QS',
  DisplacementNetFinZ: 'w',
  DisplacementNetFinY: 'w',
  DisplacementNetFinYZ: 'w',
  DisplacementInstY: 'w',
  DisplacementInstZ: 'w',
  DisplacementInstYZ: 'w',
  SupportCompression: '-',
  Vibration: 'S',
  SteelBasicCheck: 'GZT',
  SteelDisplacement: 'w',
  SteelCompression: 'N',
  SteelSupportCompression: '-',
  SteelBasePlateWeld: 'aw',
  FireProtectionRip: 'N',
}

/**
 * Resolve the short label for the check governing an element's η, or `undefined`
 * when there is none to show (unknown type, or support compression `'-'`).
 */
export const getMemberCheckShortLabel = (
  checkType: MemberCheckType | undefined,
): string | undefined => {
  if (!checkType) return undefined
  const short = memberCheckTypeToShortLabel[checkType]
  return short && short !== '-' ? short : undefined
}

/** Full readable category of a check (e.g. "zweiachsige Biegung") — shown as the η short-label tooltip. */
export const getMemberCheckCategoryLabel = (
  checkType: MemberCheckType | undefined,
): string | undefined =>
  checkType ? memberCheckTypeToReadableMap[checkType] ?? checkType : undefined

export const assemblyKindToLabel: Record<AssemblyKind, string> = {
  'clt-assembly': 'CLT (Legacy)',
  'clt-assembly-roof': 'CLT',
  'clt-assembly-ceiling': 'CLT',
  'timber-frame-assembly-wall': 'Holzrahmen',
  'timber-frame-assembly-roof': 'Holzbalken',
  'timber-frame-assembly-ceiling': 'Holzbalken',
  'timber-frame-assembly': 'Holzrahmen (Legacy)',
}

export const materialTypeOptionLabels: Record<MemberMaterialType, string> = {
  Softwood: 'Nadelholz',
  LVL: 'LVL',
  CLT: 'CLT',
  Glulam: 'Brettschichtholz',
  Steel: 'Stahl',
}
